import { defineStore } from "pinia";
import { sdk, idToChannelName } from "@/js/sdk";
import { SdkError, ERROR_CODES, type BuildStatus as SdkBuildStatus } from "@signaloid/cloud-compute-engine-client";
import { isBuildActive, isBuildTerminated, Build, BuildStatus } from "@/types/api/builds";
import { useEventBus, UseEventBusReturn } from "@vueuse/core";
import * as Sentry from "@sentry/vue";

import type { EventBusKey } from "@vueuse/core";
import { RemoteAccessState, BuildBusEvent, BuildEventE } from "@/types/general";
import { useUserStore } from "./user";

import * as util from "@/js/utilities";
import { UserLimitsE } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { useRootStore } from "@/stores/root";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { TraceVariableWithTraceType } from "@/types/api/nodes";

// SDK exposes `SubscriptionClient` only via the return type of `channel.subscribe()`.
type RealtimeSubscription = Awaited<ReturnType<ReturnType<typeof sdk.realtime.channel>["subscribe"]>>;

// Realtime frames deliver the event as a JSON string; id/status sit under its `data`.
type BuildStatusPayload = { buildId?: string; BuildID?: string; status?: string; Status?: string };
type BuildStatusEnvelope = BuildStatusPayload & { data?: BuildStatusPayload };
type BuildStatusMessage = BuildStatusPayload & {
	data?: BuildStatusPayload;
	event?: string | BuildStatusEnvelope;
};

type StoreState = {
	buildHistory: undefined | Build[];
	activeBuilds: undefined | Build[];
	buildHistoryUpdateStatus: RemoteAccessState;
	activeBuildUpdateStatus: RemoteAccessState;
	buildEventBus: UseEventBusReturn<BuildBusEvent, any>;
	buildListContinuationKey: undefined | string;
	activeBuildsListContinuationKey: undefined | string;
	realtimeSubscription: undefined | RealtimeSubscription;
	realtimeConnecting: boolean;
	realtimeTeardownRequested: boolean;
};

function extractBuildStatusUpdate(msg: BuildStatusMessage): { buildId?: string; status?: string } {
	let event: BuildStatusEnvelope | undefined;
	if (typeof msg?.event === "string") {
		try {
			event = JSON.parse(msg.event);
		} catch {
			event = undefined;
		}
	} else {
		event = msg?.event;
	}
	const payload = event?.data ?? msg?.data ?? event ?? msg;
	return {
		buildId: payload?.buildId ?? payload?.BuildID,
		status: payload?.status ?? payload?.Status,
	};
}

// SDK returns Variable.Type as a generic string; locally we expect a literal
// union driving the variable-viewer UI. Anything outside the known set is
// treated as a free-form user expression rather than discarded.
const KNOWN_TRACE_TYPES = new Set(["Variable", "FormalParameter", "UserExpression"]);
function narrowVariableTraceType(value: string): "Variable" | "FormalParameter" | "UserExpression" {
	return KNOWN_TRACE_TYPES.has(value)
		? (value as "Variable" | "FormalParameter" | "UserExpression")
		: "UserExpression";
}

export const buildKey: EventBusKey<BuildBusEvent> = Symbol("build-symbol-key");

/*
 * given n = builds returned per request, the SCDP will try to look for
 * active builds in the first (n * activeBuildMaxFetchDepth) builds in the database
 * at the time of writing n = 50
 */
const activeBuildMaxFetchDepth = 3;

export const useBuildsStore = defineStore("builds", {
	state: (): StoreState => ({
		buildHistory: undefined,
		activeBuilds: [],
		buildHistoryUpdateStatus: { loading: false, error: false, message: "" },
		activeBuildUpdateStatus: { loading: false, error: false, message: "" },
		buildListContinuationKey: undefined,
		activeBuildsListContinuationKey: undefined,
		buildEventBus: useEventBus(buildKey),
		realtimeSubscription: undefined,
		realtimeConnecting: false,
		realtimeTeardownRequested: false,
	}),
	getters: {
		getBuildByProjectID: (state) => (projectID: string) => {
			if (!state.buildHistory) {
				return null;
			}
			return state.buildHistory.find((x) => x.BuildID === projectID) ?? null;
		},
		getBuildByID: (state) => (buildID: string) => {
			if (!state.buildHistory || !buildID) {
				return null;
			}
			return state.buildHistory.find((x) => x.BuildID === buildID) ?? null;
		},
		inactiveBuilds(): Build[] {
			if (!this.buildHistory) {
				return [];
			}

			return this.buildHistory.filter((x) => !isBuildActive(x.Status));
		},
	},
	actions: {
		async getBuildByIdOrFetch(buildID: string): Promise<Build> {
			const localBuild = this.getBuildByID(buildID);
			if (localBuild) {
				return localBuild;
			}
			const response = await sdk.builds.getOne(buildID);
			const buildFromRemote = response as Build;

			this.buildHistory = this.buildHistory ? [buildFromRemote, ...this.buildHistory] : [buildFromRemote];
			return buildFromRemote;
		},
		updateBuildByID(buildData: Build) {
			if (this.buildHistory) {
				const historyIndex = this.buildHistory.findIndex((x) => x.BuildID === buildData.BuildID);
				if (historyIndex !== -1) {
					this.buildHistory.splice(historyIndex, 1, buildData);
				} else {
					this.buildHistory.push(buildData);
				}
			}
			if (this.activeBuilds) {
				const activeIndex = this.activeBuilds.findIndex((x) => x.BuildID === buildData.BuildID);
				if (activeIndex !== -1) {
					this.activeBuilds.splice(activeIndex, 1, buildData);
				} else {
					this.activeBuilds.push(buildData);
				}
			}
		},
		removeBuildByIdFromLocal(buildID: string) {
			if (this.buildHistory) {
				const newBuildHistory = this.buildHistory.filter((x) => x.BuildID != buildID);
				this.buildHistory = newBuildHistory;
			}
			if (this.activeBuilds) {
				const newActiveBuilds = this.activeBuilds.filter((x) => x.BuildID != buildID);
				this.activeBuilds = newActiveBuilds;
			}
		},
		removeFromActiveBuildList(buildID: string) {
			if (this.activeBuilds) {
				// Filter, not splice, so any duplicate copies are all removed.
				this.activeBuilds = this.activeBuilds.filter((x) => x.BuildID !== buildID);
			}
		},
		addToActiveBuildList(buildData: Build) {
			if (!this.activeBuilds) {
				return;
			}
			// Dedup by BuildID so the optimistic add can't duplicate a WS-pushed build.
			const activeIndex = this.activeBuilds.findIndex((x) => x.BuildID === buildData.BuildID);
			if (activeIndex !== -1) {
				this.activeBuilds.splice(activeIndex, 1, buildData);
			} else {
				this.activeBuilds.push(buildData);
			}
		},
		async initialiseBuildHistory() {
			// Remove all builds from local storage
			localStorage.removeItem("build-list");
			this.buildListContinuationKey = undefined;
			this.activeBuildsListContinuationKey = undefined;
			await this.fetchNextBuildBatchFromRemote();
			await this.fetchActiveBuildsFromRemote();
		},

		// Refetch the active list from scratch to recover from missed
		// WS status pushes (sleeping tab, dropped messages, etc.).
		async reconcileActiveBuilds() {
			if (this.activeBuildUpdateStatus.loading) return;
			// IDs that drop out of the active list have terminated; resolve each so
			// the status bus and history converge even when the WS missed the push.
			const previouslyActiveIds = (this.activeBuilds ?? []).map((build) => build.BuildID);
			this.activeBuildsListContinuationKey = undefined;
			await this.fetchActiveBuildsFromRemote();
			const stillActiveIds = new Set((this.activeBuilds ?? []).map((build) => build.BuildID));
			const terminatedIds = previouslyActiveIds.filter((buildID) => !stillActiveIds.has(buildID));
			await Promise.all(
				terminatedIds.map(async (buildID) => {
					// Skip if the WS already converged it (avoid double-firing completion).
					const known = this.getBuildByID(buildID);
					if (known && isBuildTerminated(known.Status)) return;
					try {
						const finalBuild = await sdk.builds.getOne(buildID);
						this.handleBuildUpdates(finalBuild as Build);
					} catch (error) {
						Sentry.captureException(error);
					}
				}),
			);
		},
		async fetchActiveBuildsFromRemote() {
			try {
				this.activeBuildUpdateStatus.loading = true;
				let requestCounter = 0;
				do {
					await this.fetchNextActiveBuildBatchFromRemote();
					requestCounter += 1;
				} while (
					requestCounter < activeBuildMaxFetchDepth &&
					this.activeBuildsListContinuationKey !== undefined
				);
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.activeBuilds = [];
				}
			} finally {
				this.activeBuildUpdateStatus.loading = false;
			}
		},
		async fetchNextActiveBuildBatchFromRemote() {
			try {
				const response = await this.fetchBuildsFromRemote(this.activeBuildsListContinuationKey, [
					"Accepted",
					"Initialising",
					"In Progress",
				]);

				// Always assign (even on empty) so a finished last build can't linger.
				const fetchedActiveBuilds = (response.Builds as Build[]) ?? [];
				this.activeBuilds =
					this.activeBuilds && this.activeBuildsListContinuationKey
						? this.activeBuilds.concat(fetchedActiveBuilds)
						: fetchedActiveBuilds;
				this.activeBuildsListContinuationKey = response.ContinuationKey;
			} catch (error) {
				let isForbiddenError = false;

				if (error instanceof SdkError) {
					if (error.code === ERROR_CODES.API_NOT_FOUND) {
						// No builds found. set the history to be empty
						this.activeBuilds = [];
					} else if (error.code === ERROR_CODES.API_FORBIDDEN) {
						isForbiddenError = true;
					}
				}
				if (!isForbiddenError) {
					this.activeBuildUpdateStatus.error = true;
					monitoringCaptureError(error, "Get active builds from remote");
				}
			}
		},

		async fetchNextBuildBatchFromRemote() {
			try {
				this.buildHistoryUpdateStatus.loading = true;
				const response = await this.fetchBuildsFromRemote(this.buildListContinuationKey);
				if (response.Builds) {
					this.buildHistory =
						this.buildHistory && this.buildListContinuationKey
							? this.buildHistory.concat(response.Builds as Build[])
							: (response.Builds as Build[]);
					this.buildListContinuationKey = response.ContinuationKey;

					this.buildHistory.sort((a, b) => b.CreatedAt - a.CreatedAt);
				}
			} catch (error) {
				let isForbiddenError = false;

				if (error instanceof SdkError) {
					if (error.code === ERROR_CODES.API_NOT_FOUND) {
						this.buildHistory = [];
					} else if (error.code === ERROR_CODES.API_FORBIDDEN) {
						isForbiddenError = true;
					}
				}
				if (!isForbiddenError) {
					this.buildHistoryUpdateStatus.error = true;
					monitoringCaptureError(error, "Get builds from remote");
				}
			} finally {
				this.buildHistoryUpdateStatus.loading = false;
			}
		},

		async fetchBuildsFromRemote(startKey?: string, buildStatus?: BuildStatus[]) {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			return sdk.builds.list({
				startKey,
				status: buildStatus as SdkBuildStatus[] | undefined,
			});
		},
		async fetchAndParseVariables(buildID: string) {
			let discoveredVariables: TraceVariableWithTraceType[] = [];
			try {
				const response = await sdk.builds.getVariables(buildID);
				if (response.Variables) {
					discoveredVariables = response.Variables.map((variableTrace): TraceVariableWithTraceType => ({
						Expression: variableTrace.Name,
						File: variableTrace.File,
						LineNumber: variableTrace.Line,
						Type: narrowVariableTraceType(variableTrace.Type),
					}));
				}
			} catch (error) {
				monitoringCaptureError(error, "Parse task variables");
			}
			return discoveredVariables;
		},
		/*
		 *	Subscribe to the user's realtime build-status channel. The push
		 *	carries buildId + status. On non-terminal transitions we merge the
		 *	status delta into the cached record and skip the REST refetch; on
		 *	terminal status (or first sighting with no cached record) we
		 *	refetch to pick up fields the WS payload doesn't carry (output
		 *	URLs, final stats). Idempotent — a second call while a
		 *	subscription is live is a no-op.
		 */
		async initRealtime(userId: string) {
			// realtimeConnecting also guards the window before subscribe() resolves and sets realtimeSubscription.
			if (this.realtimeSubscription || this.realtimeConnecting) {
				return;
			}
			this.realtimeConnecting = true;
			this.realtimeTeardownRequested = false;
			try {
				await sdk.realtime.connect();
				const channel = sdk.realtime.channel(`build-status/${idToChannelName(userId)}`);
				const subscription = await channel.subscribe();
				// Honor a teardown that raced this connect; otherwise the subscription leaks.
				if (this.realtimeTeardownRequested) {
					subscription.close();
					return;
				}
				this.realtimeSubscription = subscription;

				subscription.on("message", async (msg: BuildStatusMessage) => {
					const { buildId, status } = extractBuildStatusUpdate(msg);
					if (!buildId) return;

					const cached = this.getBuildByID(buildId);
					// Ignore pushes for an already-terminal build so we don't re-fire completion.
					if (cached && isBuildTerminated(cached.Status)) {
						return;
					}
					const isTerminal = !!status && isBuildTerminated(status as BuildStatus);
					if (cached && status && !isTerminal) {
						this.handleBuildUpdates({ ...cached, Status: status as BuildStatus });
						return;
					}

					try {
						const response = await sdk.builds.getOne(buildId);
						this.handleBuildUpdates(response as Build);
					} catch (err) {
						Sentry.captureException(err);
					}
				});
				subscription.on("error", (err: unknown) => {
					console.error("Realtime build channel error:", err);
					monitoringCaptureError(
						err instanceof Error ? err : new Error(String(err)),
						"Realtime build channel error",
						{ severity: "warning" },
					);
				});
			} catch (err) {
				console.error("Failed to initialize realtime build channel:", err);
				monitoringCaptureError(
					err instanceof Error ? err : new Error(String(err)),
					"Initialize realtime build channel",
					{ severity: "warning" },
				);
			} finally {
				this.realtimeConnecting = false;
			}
		},
		teardownRealtime() {
			// Mark teardown so an in-flight initRealtime() closes its not-yet-assigned subscription.
			this.realtimeTeardownRequested = true;
			if (this.realtimeSubscription) {
				this.realtimeSubscription.close();
				this.realtimeSubscription = undefined;
			}
		},
		handleBuildUpdates(buildData: Build) {
			// update the build object in build history
			this.updateBuildByID(buildData);

			// Check if build is terminated/completed
			if (isBuildTerminated(buildData.Status)) {
				this.removeFromActiveBuildList(buildData.BuildID);

				this.buildEventBus.emit({ type: BuildEventE.BuildCompleted, buildData: buildData });

				const rootStore = useRootStore();
				rootStore.tierLimitEventBus.emit({
					type: TierLimitEventTypeE.UsageChanged,
					affectedLimits: [
						UserLimitsE.RegistryStorageBytesMaximum,
						UserLimitsE.BuildTimeInMillisecondsCount,
						UserLimitsE.ConcurrentBuildCount,
					],
				});
				return;
			}
			// Update status of ongoing builds
			this.buildEventBus.emit({ type: BuildEventE.BuildUpdated, buildData: buildData });
		},
	},
});
